// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import {
  draftLactose,
  lactoseLevelKey,
  structuredIntolerancesOf,
} from "@/lib/constraintClarification"
import { Info } from "lucide-react"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Checkbox } from "../Checkbox"
import { Input } from "@/components/ui/input"
import { useI18n, formatEurFromCents } from "@/i18n"
import type { PantryCandidate } from "@bitebalance/contracts"
import type { PantryExtractionState } from "../useOnboardingFlow"
import type { AnswerDraft, DraftAnswers } from "../types"

interface Props {
  value: AnswerDraft
  onChange: (patch: AnswerDraft) => void
  allAnswers: DraftAnswers
  pantryExtraction: PantryExtractionState | null
  onExtract: (sourceText: string) => Promise<void>
  onConfirmCandidates: (
    candidates: Array<{ candidate: PantryCandidate; include: boolean; quantity: number }>
  ) => Promise<void>
  onClearExtraction: () => void
}

/** Step 12 (wire) — pantry quick-add + final review + personalization consent. */
export function Step12PantryReview({
  value,
  onChange,
  allAnswers,
  pantryExtraction,
  onExtract,
  onConfirmCandidates,
  onClearExtraction,
}: Props) {
  const { t } = useI18n()
  const pantryStarter = value.pantryStarter as
    "nothing_to_add_now" | Array<{ sourceText: string }> | undefined
  const reviewConfirmed = value.reviewConfirmed === true
  const [pantryText, setPantryText] = useState("")
  const [extracting, setExtracting] = useState(false)
  const [candidateState, setCandidateState] = useState<
    Record<string, { include: boolean; quantity: number }>
  >({})
  const [confirming, setConfirming] = useState(false)

  // Owner decision 2026-08-28: the pantry quick-add is OPTIONAL. "Nothing to
  // add now" is the visible pre-selected default so a user can finish
  // onboarding without touching this step; typing + "Check my items" still
  // upgrades it to real pantry items. This is a visible default the user can
  // see and change — not a silent skip.
  useEffect(() => {
    if (pantryStarter === undefined) {
      onChange({ pantryStarter: "nothing_to_add_now", reviewConfirmed })
    }
  }, [])

  async function handleExtract() {
    if (!pantryText.trim()) return
    setExtracting(true)
    try {
      await onExtract(pantryText.trim())
    } finally {
      setExtracting(false)
    }
  }

  useEffect(() => {
    if (!pantryExtraction) return
    const next: Record<string, { include: boolean; quantity: number }> = {}
    for (const c of pantryExtraction.candidates) {
      next[c.candidateId] = { include: true, quantity: c.normalizedQuantity }
    }
    setCandidateState(next)
  }, [pantryExtraction])

  async function handleConfirmCandidates() {
    if (!pantryExtraction) return
    setConfirming(true)
    try {
      const payload = pantryExtraction.candidates.map(c => ({
        candidate: c,
        include: candidateState[c.candidateId]?.include ?? true,
        quantity: candidateState[c.candidateId]?.quantity ?? c.normalizedQuantity,
      }))
      await onConfirmCandidates(payload)
      onChange({ pantryStarter: [{ sourceText: pantryExtraction.sourceText }], reviewConfirmed })
      onClearExtraction()
      setCandidateState({})
      setPantryText("")
    } finally {
      setConfirming(false)
    }
  }

  const nothingToAdd = pantryStarter === "nothing_to_add_now"

  return (
    <div className="flex flex-col gap-6">
      {!pantryExtraction && (
        <div className="flex flex-col gap-3">
          <label htmlFor="ob-pantry-text" className="text-sm font-medium text-ink">
            {t("onboarding.step12.pantryLabel")}
          </label>
          <Textarea
            id="ob-pantry-text"
            placeholder={t("onboarding.step12.pantryPlaceholder")}
            value={pantryText}
            onChange={e => setPantryText(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={!pantryText.trim() || extracting}
              onClick={handleExtract}
            >
              {extracting ? t("onboarding.shell.saving") : t("onboarding.step12.extractButton")}
            </Button>
            <Button
              type="button"
              variant={nothingToAdd ? "default" : "ghost"}
              className="min-h-11"
              onClick={() => onChange({ pantryStarter: "nothing_to_add_now", reviewConfirmed })}
            >
              {t("onboarding.step12.nothingToAdd")}
            </Button>
          </div>
          {Array.isArray(pantryStarter) && (
            <p className="text-xs text-ink-2">
              {t("onboarding.step12.candidatesTitle")}:{" "}
              {pantryStarter.map(p => p.sourceText).join("; ")}
            </p>
          )}
        </div>
      )}

      {pantryExtraction && (
        <div className="flex flex-col gap-3 rounded-bb-md border border-line bg-paper p-4">
          <p className="font-semibold text-ink">{t("onboarding.step12.candidatesTitle")}</p>
          <p className="text-sm text-ink-2">{t("onboarding.step12.candidatesBody")}</p>
          <ul className="flex flex-col gap-2">
            {pantryExtraction.candidates.map(c => {
              const state = candidateState[c.candidateId] ?? {
                include: true,
                quantity: c.normalizedQuantity,
              }
              return (
                <li
                  key={c.candidateId}
                  className="flex flex-wrap items-center gap-3 rounded-bb-md border border-line bg-cream p-3"
                >
                  <Checkbox
                    id={`ob-pantry-cand-${c.candidateId}`}
                    checked={state.include}
                    onCheckedChange={checked =>
                      setCandidateState(prev => ({
                        ...prev,
                        [c.candidateId]: { ...state, include: checked },
                      }))
                    }
                  />
                  <span className="min-w-24 font-medium text-ink">{c.displayName}</span>
                  <Input
                    type="number"
                    className="w-24"
                    value={state.quantity}
                    disabled={!state.include}
                    onChange={e =>
                      setCandidateState(prev => ({
                        ...prev,
                        [c.candidateId]: { ...state, quantity: Number(e.target.value) },
                      }))
                    }
                  />
                  <span className="text-xs text-ink-2">{c.normalizedUnit}</span>
                </li>
              )
            })}
          </ul>
          <Button
            type="button"
            className="min-h-11 self-start"
            disabled={confirming}
            onClick={handleConfirmCandidates}
          >
            {confirming ? t("onboarding.shell.saving") : t("onboarding.step12.confirmButton")}
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-3 rounded-bb-xl border border-line bg-paper p-4">
        <h2 className="font-semibold text-ink">{t("onboarding.step12.reviewTitle")}</h2>
        <div>
          <p className="text-sm font-semibold text-ink">{t("onboarding.step12.reviewHardTitle")}</p>
          <p className="text-xs text-ink-2">{t("onboarding.step12.reviewHardBody")}</p>
          <HardSummary answers={allAnswers} />
        </div>
        <div>
          <p className="text-sm font-semibold text-ink">{t("onboarding.step12.reviewSoftTitle")}</p>
          <p className="text-xs text-ink-2">{t("onboarding.step12.reviewSoftBody")}</p>
          <SoftSummary answers={allAnswers} />
        </div>
      </div>

      {/* ADR-027 rule 6: the Amy-is-not-a-doctor line, surfaced once, right
          before the final consent checkbox — quiet, dignified treatment
          matching Step6Allergens' safety notice. Extends the earlier partial
          mention in this step's consent checkbox rather than duplicating it. */}
      <div className="flex gap-3 rounded-bb-lg border border-line bg-cream p-4">
        <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-ink-2" />
        <div className="flex flex-col gap-1 text-sm">
          <p className="font-semibold text-ink">{t("onboarding.step12.notMedicalTitle")}</p>
          <p className="text-ink-2">{t("onboarding.step12.notMedicalBody")}</p>
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm text-ink">
        <Checkbox
          id="ob-consent"
          checked={reviewConfirmed}
          onCheckedChange={checked => onChange({ pantryStarter, reviewConfirmed: checked })}
        />
        <span>{t("onboarding.step12.consentLabel")}</span>
      </label>
    </div>
  )
}

function HardSummary({ answers }: { answers: DraftAnswers }) {
  const { t } = useI18n()
  const dietary = (answers[5]?.dietaryStyle as string | undefined) ?? "—"
  // ADR-080: the structured lactose intolerance with its user-chosen level
  const lactose = draftLactose(structuredIntolerancesOf(answers[6]?.structuredIntolerances))
  const lactoseText = lactose.selected
    ? `${t("moreProfile.constraints.intoleranceLactose")}${lactose.level ? ` · ${t(`moreProfile.constraints.${lactoseLevelKey(lactose.level)}`)}` : ""}`
    : null
  const allergens = (answers[6]?.allergens as string[] | "none" | undefined) ?? "none"
  const intolerances = (answers[6]?.intolerances as string[] | "none" | undefined) ?? "none"
  const neverEat = (answers[7]?.neverEat as string[] | "none" | undefined) ?? "none"
  const conditions = (answers[13]?.conditions as string[] | undefined) ?? []

  return (
    <ul className="mt-1 list-disc pl-5 text-sm text-ink">
      <li>{dietary}</li>
      <li>{allergens === "none" ? "—" : allergens.join(", ")}</li>
      {intolerances !== "none" && <li>{intolerances.join(", ")}</li>}
      {lactoseText && <li>{lactoseText}</li>}
      {neverEat !== "none" && <li>{neverEat.join(", ")}</li>}
      {conditions.length > 0 && <li>{conditions.join(", ")}</li>}
    </ul>
  )
}

function SoftSummary({ answers }: { answers: DraftAnswers }) {
  const { t, locale } = useI18n()
  const likes = (answers[7]?.likes as string[] | "none" | undefined) ?? "none"
  const skill = (answers[8]?.cookingSkill as string | undefined) ?? "—"
  const planningMode = (answers[10]?.planningMode as string | undefined) ?? "—"
  // Budget Model v2: the soft weekly spending goal (null = none set).
  const goalCents = (answers[11]?.weeklyGoalEurCents as number | null | undefined) ?? null
  const goal =
    goalCents === null
      ? t("views.budget.noGoalSet")
      : `${formatEurFromCents(goalCents, locale)} ${t("views.budget.perWeek")}`

  return (
    <ul className="mt-1 list-disc pl-5 text-sm text-ink">
      <li>{skill}</li>
      <li>{planningMode}</li>
      {likes !== "none" && <li>{likes.join(", ")}</li>}
      <li>{goal}</li>
    </ul>
  )
}
